/**
 * Every figure's cloth cage solve shares one time budget a frame. A crowd of gowned figures would otherwise each solve every frame
 * once the frame itself runs slower than a figure's own interval, and the solves alone could take half the frame. Figures ask when
 * they are drawn; at the first ask of each frame the figures that have waited longest solve until the budget is spent. The cage is an
 * offset from the skinned pose, so a figure waiting its turn still follows its bones and only its drape lags.
 */
export const CLOTH_BUDGET_MS = 1;
/** No figure solves more often than this, however much budget is left. */
export const CLOTH_SOLVE_MS = 33;
/** A figure not asking for this long (gone, hidden, culled) leaves the queue until it asks again. */
const FORGET_MS = 1000;

interface Slot { solve(): void; solvedAt: number; askedAt: number }
const slots = new Set<Slot>();
let frame: number | null = null;

/** The current animation frame's timestamp, the same for every draw in it. */
const frameStamp = () => (globalThis.document?.timeline?.currentTime as number | null | undefined) ?? performance.now();

function run(now: number) {
  const due: Slot[] = [];
  for (const slot of slots) {
    if (now - slot.askedAt > FORGET_MS) slots.delete(slot);
    else if (now - slot.solvedAt >= CLOTH_SOLVE_MS) due.push(slot);
  }
  due.sort((a, b) => a.solvedAt - b.solvedAt);
  const began = performance.now();
  for (const slot of due) {
    // At least one figure solves each frame, so a budget smaller than one solve still makes progress.
    if (slot !== due[0] && performance.now() - began >= CLOTH_BUDGET_MS) break;
    slot.solvedAt = now; slot.solve();
  }
}

/** A figure's place in the shared queue: call the returned function whenever the figure is drawn. */
export function clothSlot(solve: () => void) {
  // A new figure has waited longest, so its first solve comes at once.
  const slot: Slot = { solve, solvedAt: -Infinity, askedAt: 0 };
  return () => {
    const now = frameStamp();
    slot.askedAt = now; slots.add(slot);
    if (now !== frame) { frame = now; run(now); }
  };
}
